'use client';

import { useEffect, useRef } from 'react';

export default function IkigaiSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = sectionRef?.current?.querySelectorAll('.reveal-hidden, .reveal-hidden-left, .reveal-hidden-right');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="methode" className="py-28 px-6 bg-cream overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20 reveal-hidden">
          <span className="badge-sand rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest inline-block mb-5">
            Die Methode
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal leading-tight max-w-3xl mx-auto">
            Die Ikiguide-Methode:{' '}
            <span className="italic text-sage">Wo deine Idee auf den Markt trifft.</span>
          </h2>
          <p className="mt-5 text-muted text-lg max-w-2xl mx-auto font-light leading-relaxed">
            Wir nehmen nicht irgendeine Idee – wir bauen deine Gründung um das auf, was du liebst,
            gut kannst und wofür ein echter Markt existiert.
          </p>
        </div>

        {/* Main layout: SVG + explanation */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Ikigai SVG Diagram */}
          <div className="reveal-hidden-left flex justify-center">
            <div className="relative w-full max-w-[480px] aspect-square">
              <svg
                viewBox="0 0 480 480"
                className="w-full h-full drop-shadow-lg"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Was du liebst (top-left) */}
                <circle
                  cx="200" cy="200" r="130"
                  fill="rgba(107,143,110,0.18)"
                  stroke="rgba(107,143,110,0.5)"
                  strokeWidth="1.5"
                  className="ikigai-circle"
                  style={{ animationDelay: '0s' }}
                />
                {/* Was du gut kannst (top-right) */}
                <circle
                  cx="280" cy="200" r="130"
                  fill="rgba(91,127,166,0.15)"
                  stroke="rgba(91,127,166,0.45)"
                  strokeWidth="1.5"
                  className="ikigai-circle"
                  style={{ animationDelay: '0.5s' }}
                />
                {/* Was die Welt braucht (bottom-left) */}
                <circle
                  cx="200" cy="280" r="130"
                  fill="rgba(212,184,150,0.2)"
                  stroke="rgba(184,149,106,0.45)"
                  strokeWidth="1.5"
                  className="ikigai-circle"
                  style={{ animationDelay: '1s' }}
                />
                {/* Wofür du bezahlt werden kannst (bottom-right) */}
                <circle
                  cx="280" cy="280" r="130"
                  fill="rgba(107,143,110,0.12)"
                  stroke="rgba(74,107,77,0.4)"
                  strokeWidth="1.5"
                  className="ikigai-circle"
                  style={{ animationDelay: '1.5s' }}
                />

                {/* Center Ikigai glow */}
                <circle
                  cx="240" cy="240" r="40"
                  fill="rgba(107,143,110,0.35)"
                  stroke="rgba(74,107,77,0.7)"
                  strokeWidth="2"
                  style={{ animation: 'ikigaiPulse 2s ease-in-out infinite' }}
                />
                <circle
                  cx="240" cy="240" r="28"
                  fill="rgba(74,107,77,0.6)"
                />

                {/* Center text */}
                <text x="240" y="236" textAnchor="middle" fill="white" fontSize="9" fontFamily="Plus Jakarta Sans" fontWeight="700" letterSpacing="1">
                  IKIGAI
                </text>
                <text x="240" y="249" textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize="7" fontFamily="Plus Jakarta Sans">
                  生き甲斐
                </text>

                {/* Labels - Was du liebst */}
                <text x="160" y="155" textAnchor="middle" fill="#4A6B4D" fontSize="10" fontFamily="Plus Jakarta Sans" fontWeight="700">
                  Was du liebst
                </text>
                <text x="160" y="167" textAnchor="middle" fill="#6B8F6E" fontSize="8.5" fontFamily="Plus Jakarta Sans" fontStyle="italic">
                  Passion
                </text>

                {/* Was du gut kannst */}
                <text x="320" y="155" textAnchor="middle" fill="#3D5F82" fontSize="10" fontFamily="Plus Jakarta Sans" fontWeight="700">
                  Was du gut kannst
                </text>
                <text x="320" y="167" textAnchor="middle" fill="#5B7FA6" fontSize="8.5" fontFamily="Plus Jakarta Sans" fontStyle="italic">
                  Vocation
                </text>

                {/* Was die Welt braucht */}
                <text x="160" y="330" textAnchor="middle" fill="#B8956A" fontSize="10" fontFamily="Plus Jakarta Sans" fontWeight="700">
                  Was die Welt braucht
                </text>
                <text x="160" y="342" textAnchor="middle" fill="#D4B896" fontSize="8.5" fontFamily="Plus Jakarta Sans" fontStyle="italic">
                  Mission
                </text>

                {/* Wofür du bezahlt wirst */}
                <text x="320" y="330" textAnchor="middle" fill="#4A6B4D" fontSize="10" fontFamily="Plus Jakarta Sans" fontWeight="700">
                  Wofür du bezahlt wirst
                </text>
                <text x="320" y="342" textAnchor="middle" fill="#6B8F6E" fontSize="8.5" fontFamily="Plus Jakarta Sans" fontStyle="italic">
                  Profession
                </text>

                {/* Intersection labels */}
                <text x="240" y="188" textAnchor="middle" fill="#5B7FA6" fontSize="7.5" fontFamily="Plus Jakarta Sans" fontWeight="600">
                  Begeisterung
                </text>
                <text x="183" y="243" textAnchor="middle" fill="#B8956A" fontSize="7.5" fontFamily="Plus Jakarta Sans" fontWeight="600">
                  Berufung
                </text>
                <text x="297" y="243" textAnchor="middle" fill="#4A6B4D" fontSize="7.5" fontFamily="Plus Jakarta Sans" fontWeight="600">
                  Karriere
                </text>
                <text x="240" y="295" textAnchor="middle" fill="#6B8F6E" fontSize="7.5" fontFamily="Plus Jakarta Sans" fontWeight="600">
                  Erfüllung
                </text>
              </svg>
            </div>
          </div>

          {/* Right: Explanation */}
          <div className="flex flex-col gap-8 reveal-hidden-right">
            <div className="flex flex-col gap-6">
              {[
                {
                  color: 'bg-sage/15 text-sage-dark',
                  title: 'Was du liebst — Passion',
                  desc: 'Die Tätigkeiten und Bereiche, die dich lebendig fühlen lassen. Nicht was du tun solltest – was dich wirklich entzündet.',
                },
                {
                  color: 'bg-slate-blue/10 text-slate-blue-dark',
                  title: 'Was du gut kannst — Vocation',
                  desc: 'Deine Talente, Fähigkeiten und Erfahrungen. Was du durch Übung, Ausbildung und Lebensweg entwickelt hast.',
                },
                {
                  color: 'bg-sand-light/40 text-sand-dark',
                  title: 'Was die Welt braucht — Mission',
                  desc: 'Die Bedürfnisse der Gesellschaft, zu denen du beitragen möchtest. Dein Sinn im großen Ganzen.',
                },
                {
                  color: 'bg-sage/10 text-sage-dark',
                  title: 'Wofür du bezahlt werden kannst — Profession',
                  desc: 'Wo deine Fähigkeiten auf Marktbedarf treffen. Die wirtschaftliche Realität deiner Berufung.',
                },
              ]?.map((item, i) => (
                <div key={i} className={`reveal-hidden delay-${(i + 1) * 100} flex gap-4 items-start`}>
                  <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${item?.color?.split(' ')?.[0]}`} />
                  <div>
                    <h4 className={`text-sm font-bold mb-1 ${item?.color?.split(' ')?.[1]}`}>{item?.title}</h4>
                    <p className="text-muted text-sm leading-relaxed font-light">{item?.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Key message box */}
            <div className="bg-sage/8 border border-sage/20 rounded-3xl p-6 reveal-hidden delay-400">
              <p className="text-charcoal text-base leading-relaxed font-light">
                <span className="font-semibold text-sage-dark">Die Ikiguide-Methode</span> geht über reine Reflexion hinaus.
                Wir finden nicht nur deine ideale Geschäftsidee – wir entwickeln eine konkrete
                Gründungsstrategie, die deine Stärken mit echter Marktchance verbindet.
              </p>
            </div>

            <a
              href="#cta"
              className="btn-primary text-sm font-semibold px-7 py-3.5 rounded-full inline-block text-center w-fit"
            >
              Meine Gründungsidee finden →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}